import React from "react";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from "@mui/material/FormControlLabel";
import Paper from "@mui/material/Paper";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Grid } from "@client/shared/components";
import { globalStyles, noop } from "@client/shared/constants";
import style from "./style";

const RecipientsPanelView = ({
  isAllRecipients = false,
  siteList = [],
  departmentList = [],
  userRoleList = [],
  individualContactList = [],
  recipientSites = [],
  recipientDepartments = [],
  recipientRoles = [],
  recipientSelectedContacts = [],
  recipients = [],
  columnConfig = [],
  notifyRecipient = false,
  roleAccess = {},
  handleChange = noop,
  updateRecipientList = noop,
}) => {
  
  // fixing material ui 5.* duplicate label issue
  const getList = (list = [], key) => {
    return list.map((item, index, arr) => {
      if (arr.findIndex(value => (value[key] === item[key])) !== index) {
        item[key] += ' ';
      }
      return item;
    })
  };

  return (
    <>
      <Box
        sx={{
          mt: 0,
        }}
      >
        <Typography variant="body2" sx={{ mb: 1 }}>
          Select all extensions on the account or select individual sites, departments, roles<br/> or individuals below.
        </Typography>
        <FormControlLabel
          label={
            <Typography
              variant="body2"
              sx={{
                ...globalStyles.text.weight.bold
              }}
            >
              All Extensions
            </Typography>
          }
          control={
            <Checkbox
              checked={isAllRecipients}
              onChange={evt => {
                const { checked } = evt.target;
                handleChange({
                  name: 'isAllRecipients',
                  value: checked
                }, 'checkbox');
                if (checked) {
                  ['recipientSites', 'recipientDepartments', 'recipientRoles', 'recipientSelectedContacts', 'recipients'].forEach(name => {
                    handleChange({ name, value: [] }, "autocomplete");
                  });
                }
              }}
            />
          } 
        />
      </Box>
      <Box
        sx={{
          mt: 2,
          ...globalStyles.display.flex,
          ...globalStyles.flex.justify.between,
        }}
      >
        <Autocomplete
          disableCloseOnSelect
          disabled={isAllRecipients}
          fullWidth
          multiple
          size="small"
          value={recipientSites}
          options={siteList}
          getOptionLabel={(option) => option || ""}
          onChange={(evt, value, reason) => {
            const site = (reason === 'removeOption' || reason === 'clear')
              ? recipientSites.filter(site => !value.includes(site))
              : value.filter(site => !recipientSites.includes(site))
            updateRecipientList('SITES', reason, site);
            handleChange({ name: "recipientSites", value }, "autocomplete");
          }}
          renderInput={(params) => (
            <TextField {...params} placeholder="Select Sites" />
          )}
          ListboxProps={{
            sx: {...style.autocompleteOptions }
          }}
        />
      </Box>
      <Box
        sx={{
          ...globalStyles.display.flex,
          ...globalStyles.flex.justify.between,
          mt: 2,
        }}
      >
        <Autocomplete
          disableCloseOnSelect
          disabled={isAllRecipients}
          fullWidth
          size="small"
          multiple
          value={recipientDepartments}
          options={departmentList}
          getOptionLabel={(option) => option || ""}
          onChange={(evt, value, reason) => {
            const department = (reason === 'removeOption' || reason ==='clear')
              ? recipientDepartments.filter(department => !value.includes(department))
              : value.filter(department => !recipientDepartments.includes(department))
            updateRecipientList('DEPARTMENTS', reason, department);
            handleChange(
              { name: "recipientDepartments", value },
              "autocomplete"
            );
          }}
          renderInput={(params) => (
            <TextField {...params} placeholder="Select Departments" />
          )}
          ListboxProps={{
            sx: {...style.autocompleteOptions }
          }}
        />
      </Box>
      <Box
        sx={{
          ...globalStyles.display.flex,
          ...globalStyles.flex.justify.between,
          mt: 2,
        }}
      >
        <Autocomplete
          disableCloseOnSelect
          disabled={isAllRecipients}
          fullWidth
          multiple
          size="small"
          value={recipientRoles}
          options={getList(userRoleList, 'displayName')}
          getOptionLabel={(option) => option.displayName || ""}
          onChange={(evt, value, reason) => {
            const role = (reason === 'removeOption' || reason === 'clear')
              ? recipientRoles.filter(role => !value.some(item => item.id === role.id))
              : value.filter(role => !recipientRoles.some(item => item.id === role.id))
            updateRecipientList('ROLES', reason, role);
            handleChange({ name: "recipientRoles", value }, "autocomplete");
          }}
          renderInput={(params) => (
            <TextField {...params} placeholder="Select Roles" />
          )}
          ListboxProps={{
            sx: {...style.autocompleteOptions }
          }}
        />
      </Box>
      {roleAccess?.sendToIndividuals && (
         <Box
          sx={{
            ...globalStyles.display.flex,
            ...globalStyles.flex.justify.between,
            mt: 2,
          }}
        >
          <Autocomplete
            disableCloseOnSelect
            disabled={isAllRecipients}
            fullWidth
            multiple
            size="small"
            value={recipientSelectedContacts}
            options={getList(individualContactList, 'name')}
            getOptionLabel={(option) => option.name || "-"}
            onChange={(evt, value, reason) => {
              const indiviualContact = (reason === 'removeOption' || reason === 'clear')
                ? recipientSelectedContacts.filter(indiviualContact => !value.some(item => item.id === indiviualContact.id))
                : value.filter(indiviualContact => !recipientSelectedContacts.some(item => item.id === indiviualContact.id))
              updateRecipientList('INDIVIDIUAL_CONTACTS', reason, indiviualContact);
              handleChange(
                { name: "recipientSelectedContacts", value },
                "autocomplete"
              );
            }}
            renderInput={(params) => (
              <TextField {...params} placeholder="Select Individual Contacts" />
            )}
            ListboxProps={{
              sx: {...style.autocompleteOptions }
            }}
          />
        </Box>
      )}
      {(!!recipients.length && !isAllRecipients) && (
        <Paper
          elevation={0}
          sx={{
            mt: 2,
            ...style.gridPaper,
          }}
        >
          <Grid columns={columnConfig} rows={recipients} hasSelection={false} />
        </Paper>
      )}
      <Box
        sx={{
          mt: 6,
        }}
      >
        <Typography variant="h6" color="secondary">
          Notify Recipients
        </Typography>
        <Typography variant="body2">
          Users will receive a "New Voicemail" notification on their device.
        </Typography>
        <FormControlLabel
          label={
            <Typography
              variant="body2"
              sx={{
                ml: 1,
              }}
            >
              {notifyRecipient ? "Enabled" : "Disabled"}
            </Typography>
          }
          value="female"
          control={
            <Switch
              checked={notifyRecipient}
              onChange={(evt) => {
                const { checked } = evt.target;
                handleChange(
                  {
                    name: "notifyRecipient",
                    value: checked,
                  },
                  "checkbox"
                );
              }}
            />
          }
          sx={{
            mt: 2,
            ml: 0.1,
          }}
        />
      </Box>
    </>
  );
};

export default RecipientsPanelView;
